import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Button, Tag } from 'antd';
import {
  MessageOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  FieldTimeOutlined,
  DownOutlined,
  UpOutlined,
  CheckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  STATUS_CONFIG,
  URGENCY_CONFIG,
  formatDueDate,
  getRelativeTime,
} from '../../utils/taskTableUtils';
import { getLastCommentUser, isRestrictedTaskLevel } from '../../utils/task.utils';
import { space } from '../../configs/designTokens';

/**
 * Skeleton Loading Component for Mobile Task Cards
 */
export const TaskCardSkeleton = ({ count = 5 }) => (
  <div className="task-card-skeleton" role="status" aria-label="Loading tasks">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="task-card-skeleton__card">
        <div className="task-card-skeleton__header">
          <Skeleton.Avatar active size={8} shape="circle" />
          <Skeleton.Button active size="small" style={{ width: 70, height: 18, minWidth: 70 }} />
          <Skeleton.Input active size="small" style={{ width: '60%' }} />
        </div>
        <div className="task-card-skeleton__meta">
          <Skeleton.Input active size="small" style={{ width: 60 }} />
          <Skeleton.Avatar active size={20} />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Shared mobile card view for task lists.
 * Used by TaskBoardTable, UnassignedTasksView, and ProjectTasksList.
 *
 * Optional features (enabled by passing props):
 * - Selection: selectedIds + onSelectionChange
 * - Remove action: onRemoveTask
 * - Milestones: milestones array (shown in expanded details)
 */
const TaskMobileCards = ({
  tasks,
  loading,
  onTaskClick,
  level,
  selectedIds,
  onSelectionChange,
  onRemoveTask,
  milestones,
}) => {
  const [expandedCards, setExpandedCards] = useState(new Set());
  const selectable = !!(selectedIds && onSelectionChange);

  const getTaskMilestone = (taskId) => {
    if (!milestones) return null;
    return milestones.find((m) => m.tasks?.some((t) => t.id === taskId));
  };

  if (loading && tasks.length === 0) {
    return <TaskCardSkeleton count={5} />;
  }

  if (!loading && tasks.length === 0) {
    return <div className="task-board-card__empty">No tasks found</div>;
  }

  return (
    <div className="task-board-card__list">
      {tasks.map((task) => {
        const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.NOT_STARTED;
        const urgencyConfig = URGENCY_CONFIG[task.urgency] || URGENCY_CONFIG.LOW;
        const { text: dueText, isOverdue, daysLate } = formatDueDate(task.dueDate, task.status);
        const isCompleted = ['COMPLETED', 'DONE', 'NO_LONGER_NEEDED', 'DUPLICATE'].includes(task.status);

        const assigneeList = task.assigneeUsers || (task.assigneeUser ? [task.assigneeUser] : []);
        const primaryAssignee = assigneeList[0];
        const overflowCount = assigneeList.length > 1 ? assigneeList.length - 1 : 0;

        // Awaiting response logic (matches table assignee column)
        const lastCommentUser = getLastCommentUser(task);
        const isLastCommenterRequestor = lastCommentUser &&
          lastCommentUser.id === (task.createdBy && task.createdBy.id);
        const showAwaiting = assigneeList.length > 0 && (
          isLastCommenterRequestor ||
          !task.attachedComments ||
          (Array.isArray(task.attachedComments) && task.attachedComments.length === 0)
        );

        const isExpanded = expandedCards.has(task.id);
        const isRecurring = task.isRecurring || task.recurring;
        const isSelected = selectable && selectedIds.includes(task.id);
        const milestone = getTaskMilestone(task.id);

        const handleClick = (e) => {
          if (e.ctrlKey || e.metaKey || e.button === 1) return;
          e.preventDefault();
          onTaskClick(task.id);
        };

        const handleExpand = (e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpandedCards((prev) => {
            const next = new Set(prev);
            if (next.has(task.id)) {
              next.delete(task.id);
            } else {
              next.add(task.id);
            }
            return next;
          });
        };

        const handleToggleSelect = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isSelected) {
            onSelectionChange(selectedIds.filter((id) => id !== task.id));
          } else {
            onSelectionChange([...selectedIds, task.id]);
          }
        };

        const handleRemove = (e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemoveTask(task.id, task.name);
        };

        const cardClass = [
          'task-board-card',
          isOverdue && !isCompleted && 'task-board-card--overdue',
          isCompleted && 'task-board-card--completed',
          isExpanded && 'task-board-card--expanded',
          isSelected && 'task-board-card--selected',
        ].filter(Boolean).join(' ');

        const requestorName = task.createdBy?.name || task.createdBy?.username || '';
        const orgName = task.createdBy?.roles?.[0]?.org?.name;

        return (
          <a
            key={task.id}
            href={`/tasks/details/${task.id}`}
            className={cardClass}
            onClick={handleClick}
          >
            {selectable && (
              <div
                className="task-board-card__select"
                onClick={handleToggleSelect}
                role="checkbox"
                aria-checked={isSelected}
              >
                <div className={`task-board-card__checkbox ${isSelected ? 'task-board-card__checkbox--checked' : ''}`}>
                  {isSelected && <CheckOutlined />}
                </div>
              </div>
            )}
            <div className={`task-board-card__urgency-bar task-board-card__urgency-bar--${urgencyConfig.cssClass}`} />
            <div className="task-board-card__content">
              <div className="task-board-card__row-primary">
                <div className="task-board-card__name">
                  {task.name || 'Untitled Task'}
                  {isRecurring && <SyncOutlined />}
                </div>
                <span className={`task-board-card__status task-board-card__status--${statusConfig.cssClass}`}>
                  {statusConfig.label}
                </span>
              </div>
              <div className="task-board-card__row-meta">
                {isOverdue ? (
                  <span className="task-board-card__overdue-pill">
                    <ExclamationCircleOutlined />
                    {daysLate}d late
                  </span>
                ) : dueText !== '—' ? (
                  <span className="task-board-card__due">{dueText}</span>
                ) : null}

                {primaryAssignee && (
                  <span className="task-board-card__assignee">
                    <AvatarS3
                      imgKey={primaryAssignee.avatar?.key}
                      username={primaryAssignee.name || primaryAssignee.username || ''}
                      size={18}
                    />
                    <span>{(primaryAssignee.name || primaryAssignee.username || '').split(' ')[0]}</span>
                    {overflowCount > 0 && (
                      <span className="task-board-card__assignee-overflow">+{overflowCount}</span>
                    )}
                  </span>
                )}

                {showAwaiting && (
                  <FieldTimeOutlined className="task-board-card__awaiting" />
                )}

                <span className="task-board-card__spacer" />

                {task.comments?.length > 0 && (
                  <span className="task-board-card__comments">
                    <MessageOutlined />
                    {task.comments.length}
                  </span>
                )}

                {task.updatedAt && (
                  <span className="task-board-card__updated">
                    {getRelativeTime(task.updatedAt)}
                  </span>
                )}

                <span
                  className="task-board-card__expand"
                  onClick={handleExpand}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-label="Show details"
                >
                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                </span>
              </div>

              {isExpanded && (
                <div className="task-board-card__details">
                  {requestorName && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label">Requestor</span>
                      <span className="task-board-card__detail-value">
                        <AvatarS3
                          imgKey={task.createdBy?.avatar?.key}
                          username={requestorName}
                          size={18}
                        />
                        {requestorName}
                      </span>
                    </div>
                  )}
                  {task.requestType && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label">Type</span>
                      <span className="task-board-card__detail-value">
                        {task.requestType}
                        {task.requestSubType && ` / ${task.requestSubType}`}
                      </span>
                    </div>
                  )}
                  {milestone && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label">Milestone</span>
                      <span className="task-board-card__detail-value">
                        <Tag color="blue" style={{ margin: space[0] }}>{milestone.name}</Tag>
                      </span>
                    </div>
                  )}
                  {!isRestrictedTaskLevel(level) && orgName && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label">Advertiser</span>
                      <span className="task-board-card__detail-value">{orgName}</span>
                    </div>
                  )}
                  {task.createdAt && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label">Created</span>
                      <span className="task-board-card__detail-value">
                        {new Date(task.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {onRemoveTask && (
                    <div className="task-board-card__detail-row">
                      <span className="task-board-card__detail-label" />
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={handleRemove}
                        className="task-board-card__remove"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
};

TaskMobileCards.propTypes = {
  tasks: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onTaskClick: PropTypes.func.isRequired,
  level: PropTypes.string,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
  onRemoveTask: PropTypes.func,
  milestones: PropTypes.array,
};

TaskMobileCards.defaultProps = {
  loading: false,
  level: '',
};

export default TaskMobileCards;
